import React, { useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import '../../../styles/medics.scss';
import {
  fetchDoctor,
  fetchSingleDoctorSummary,
  getMedicOrders,
  getDoctorReferralChain,
  markDoctorPayoutEntryStatus,
  markDoctorPayoutEntriesBulk,
} from '../../../api/medics-api';
import { toast } from 'react-toastify';
import { Container } from '../../../components/content';
import ContentLayout from '../../../layout/ContentLayout';
import styles from '../../../styles/viewmedic.module.scss';
import GenericTable from '../../../components/tables/GenericTable';
import Pagination from '../../../components/layout/Pagination';
import dayjs from 'dayjs';
import { formatPriceWithCurrency } from '../../../utils/formatPrice';
import { Button, ConfigProvider, DatePicker, Tabs } from 'antd';
import {
  PAYOUT_STATUSES,
  resolvePayoutStatusKey,
  formatPayoutStatusAsPill,
  payoutStatusLabel,
} from '../../../utils/formatPayoutStatus';
import BulkMarkPaidConfirmModal from '../../../components/modals/BulkMarkPaidConfirmModal';
import DirectReferralsModal from '../../../components/modals/DirectReferralsModal';
import DoctorCharityEarnings from '../../../components/doctor/DoctorCharityEarnings';
import { usePermissions } from '../../../context/PermissionsContext';

const { RangePicker } = DatePicker;

// The purchase table is split per payout state instead of filtered through a
// dropdown. 'pending' on the API means "not paid yet", so undelivered orders
// live in that tab too — their pill still reads "Nije isporučeno".
const payoutTabItems = [
  { key: 'pending', label: 'Čeka realizaciju' },
  { key: 'paid', label: 'Realizovano' },
];

const ViewMedic = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = params;
  // Clinic owners open this page for their own mentors, read-only: no payout
  // marking and no links into admin-only order pages. "Admin" here means anyone holding
  // the network permissions with platform-wide scope.
  const { canAll } = usePermissions();
  const isAdmin = canAll(['network:manage', 'scope:all']);
  const backTo =
    location.state?.backTo ||
    (isAdmin ? '/korisnici?tab=mentori' : '/clinic-medic');
  const [currentPageOrders, setCurrentPageOrders] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [totalPagesOrders, setTotalPagesOrders] = useState(1);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [doctorSummary, setDoctorSummary] = useState(null);
  const [ownCommissionPercent, setOwnCommissionPercent] = useState(null);
  const [doctorOrders, setDoctorOrders] = useState([]);

  const [filter, setFilter] = useState({
    createdFrom: null,
    createdTo: null,
  });
  const [activePayoutTab, setActivePayoutTab] = useState(
    searchParams.get('tab') === 'paid' ? 'paid' : 'pending'
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [isDirectReferralsModalOpen, setIsDirectReferralsModalOpen] =
    useState(false);

  const onPageChangeOrders = (page) => {
    setCurrentPageOrders(page);
  };

  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    clinic: null,
  });

  const dataMedicInfo = [
    {
      organizacija: '',
      kupovine: 0,
      suma: '',
      provizija: '',
      datum: '',
    },
  ];
  const columnsMedicInfo = [
    {
      key: 'organizacija',
      label: 'Organizacija',
      render: (val, row) => {
        return (
          <span>{doctorInfo?.clinicName ? doctorInfo?.clinicName : ''}</span>
        );
      },
    },
    {
      key: 'kupovine',
      label: 'Ukupan broj kupovina',
      render: (val, row) => {
        return <span>{doctorSummary?.totalOrders || '0'}</span>;
      },
    },
    {
      key: 'suma',
      label: 'Suma svih kupovina',
      render: (val, row) => {
        return (
          <span>
            {formatPriceWithCurrency(doctorSummary?.totalRevenue || 0)}
          </span>
        );
      },
    },
    {
      key: 'provizija',
      label: 'Provizija',
      render: (val, row) => {
        return (
          <span>
            {formatPriceWithCurrency(doctorSummary?.totalCommissionDoctor)}
          </span>
        );
      },
    },
    {
      key: 'datum',
      label: 'Datum dodavanja mentora',
      render: (val, row) => {
        const not_null_val = doctorInfo?.createdAt;
        const date = dayjs(not_null_val).format('DD.MM.YYYY.');
        return <span>{date || '-'}</span>;
      },
    },
  ];

  const isRowMarkable = (row) =>
    !!row?.payoutEntryId && resolvePayoutStatusKey(row) === 'pending';

  const handleFetchDoctorSummary = async () => {
    const { data, success, message } = await fetchSingleDoctorSummary(id);
    if (success) {
      setDoctorSummary(data);
    }
  };

  const handleFetchDoctorOrders = async () => {
    const { data, success, message } = await getMedicOrders(id, {
      page: currentPageOrders,
      limit: 10,
      createdFrom: filter.createdFrom || undefined,
      createdTo: filter.createdTo || undefined,
      payoutStatus: activePayoutTab,
    });

    if (!success) {
      toast.error(message || 'Error while fetching the data!');
      return;
    }

    setDoctorOrders(data.items || []);
    setTotalPagesOrders(data.totalPages || 1);
    setCurrentPageOrders(data.currentPage || 1);
    setSelectedRowKeys([]);
  };

  const handleFetchDoctorInfo = async () => {
    const { data, success, message } = await fetchDoctor(id);

    if (!success) {
      toast.error(message || 'Error while fetching the data!');
      return;
    }

    // /doctors/:id returns the clinic populated, so no separate clinic fetch.
    setDoctorInfo({ ...data, clinicName: data?.clinic?.name });
  };

  const handleFetchOwnCommissionPercent = async () => {
    const { data, success } = await getDoctorReferralChain(id);
    if (!success) return;
    // effectivePercent already accounts for the clinic-affiliation override
    // (a doctor tied to a clinic earns the clinic-adjusted rate, not their own).
    setOwnCommissionPercent(data?.effectivePercent ?? null);
  };

  useEffect(() => {
    setSearchParams(
      { page: currentPageOrders, tab: activePayoutTab },
      // Keep state so the back link target survives the URL rewrite.
      { replace: true, state: location.state }
    );
  }, [currentPageOrders, activePayoutTab]); // eslint-disable-line

  useEffect(() => {
    handleFetchDoctorInfo();
    handleFetchDoctorSummary();
    handleFetchOwnCommissionPercent();
  }, []);

  useEffect(() => {
    handleFetchDoctorOrders();
  }, [
    currentPageOrders,
    filter.createdFrom,
    filter.createdTo,
    activePayoutTab,
  ]);

  const handleDateRangeChange = (dates) => {
    setFilter((prev) => ({
      ...prev,
      createdFrom: dates?.[0] ? dates[0].startOf('day').toISOString() : null,
      createdTo: dates?.[1] ? dates[1].endOf('day').toISOString() : null,
    }));
    setCurrentPageOrders(1);
  };

  const handlePayoutTabChange = (key) => {
    setActivePayoutTab(key);
    setCurrentPageOrders(1);
    setSelectedRowKeys([]);
    // Otherwise the previous tab's rows linger under the new tab until the
    // fetch resolves.
    setDoctorOrders([]);
  };

  const handleMarkEntryStatus = async (row, status) => {
    const { success, message } = await markDoctorPayoutEntryStatus(
      id,
      row.payoutEntryId,
      status
    );
    if (!success) {
      toast.error(
        message || 'Došlo je do greške pri izmeni statusa provizije.'
      );
      return;
    }
    toast.success('Status provizije je ažuriran.');
    handleFetchDoctorOrders();
  };

  const handleBulkMarkPaid = async () => {
    // selectedRowKeys are row _ids (GenericTable's row selection always keys
    // off row._id — one row per order/product commission line, not the order id),
    // but the bulk-status endpoint operates on commission-ledger entry ids — map
    // through the loaded rows to get the right ids, and only ones that actually
    // have a markable pending entry.
    const entryIds = doctorOrders
      .filter((row) => selectedRowKeys.includes(row._id) && isRowMarkable(row))
      .map((row) => row.payoutEntryId);

    if (entryIds.length === 0) {
      toast.error(
        'Nijedna od izabranih stavki nema proviziju koja čeka realizaciju.'
      );
      return;
    }

    setBulkUpdating(true);
    const { success, message } = await markDoctorPayoutEntriesBulk(
      id,
      entryIds,
      'paid'
    );
    setBulkUpdating(false);
    if (!success) {
      toast.error(
        message || 'Došlo je do greške pri izmeni statusa provizije.'
      );
      return;
    }
    toast.success('Provizija je označena kao realizovana.');
    setSelectedRowKeys([]);
    setBulkConfirmOpen(false);
    handleFetchDoctorOrders();
  };

  const columnsOrdersInfo = [
    {
      key: 'imekupca',
      label: 'Ime kupca',
      render: (val, row) => {
        return <span>{row?.customer?.fullName} </span>;
      },
    },
    /*{
      key: 'idkupca',
      label: 'ID kupca',
      render: (val, row) => {
        return <span>{row?._id} </span>;
      },
    },*/
    /*{
      key: 'email',
      label: 'Email',
      render: (val, row) => {
        return <span>{row?.customer?.email} </span>;
      },
    },*/
    {
      key: 'productName',
      label: 'Proizvod',
      render: (val) => <span>{val || 'Cela porudžbina'}</span>,
    },
    {
      key: 'vrednost',
      label: 'Osnovica za proviziju',
      render: (val, row) => {
        return <span>{formatPriceWithCurrency(row?.commissionBase || 0)}</span>;
      },
    },
    {
      key: 'provizija',
      label: 'Provizija',
      render: (val, row) => {
        return (
          <span>{formatPriceWithCurrency(row?.commissionDoctor || 0)}</span>
        );
      },
    },
    {
      key: 'percent',
      label: 'Procenat provizije',
      render: (val) => <span>{val != null ? `${val}%` : '-'}</span>,
    },
    {
      key: 'level',
      label: 'Nivo',
      render: (val) => <span>{val != null ? val : '-'}</span>,
    },
    {
      key: 'chainOwnerName',
      label: 'Iskorišten kod od',
      render: (val) => <span>{val || '-'}</span>,
    },
    {
      key: 'payoutStatus',
      label: 'Status provizije',
      render: (val, row) => formatPayoutStatusAsPill(row),
    },
    {
      key: 'datum',
      label: 'Datum kupovine',
      render: (val, row) => {
        const not_null_val = row?.createdAt;
        const date = dayjs(not_null_val).format('DD.MM.YYYY.');
        return <span>{date || '-'}</span>;
      },
    },
  ];

  const orderActions = !isAdmin
    ? []
    : [
        {
          key: 'go-to-order',
          label: 'Idi na porudžbinu',
          icon: <img src="/assets/icons/view-icon.svg" alt="View" />,
          onClick: (row) =>
            navigate(`/order/${row.orderId || row._id}`, {
              state: { from: location.pathname + location.search },
            }),
        },
        {
          key: 'mark-paid',
          label: 'Označi proviziju kao realizovanu',
          visible: (row) => isRowMarkable(row),
          onClick: (row) => handleMarkEntryStatus(row, 'paid'),
        },
        {
          key: 'mark-pending',
          label: 'Označi proviziju kao nerealizovanu',
          visible: (row) =>
            !!row?.payoutEntryId && row?.payoutStatus === 'paid',
          onClick: (row) => handleMarkEntryStatus(row, 'pending'),
        },
      ];

  const legendStatuses = PAYOUT_STATUSES.filter((s) =>
    activePayoutTab === 'paid' ? s.key === 'paid' : s.key !== 'paid'
  );

  const selectedMarkableRows = doctorOrders.filter(
    (row) => selectedRowKeys.includes(row._id) && isRowMarkable(row)
  );
  const selectedMarkableTotal = selectedMarkableRows.reduce(
    (sum, row) => sum + (row.commissionDoctor || 0),
    0
  );

  return (
    <>
      <Container className={styles.medic}>
        <Link to={backTo} className="link_btn">
          <img alt="back image" src="/assets/icons/chevron.svg" />
          <span>Nazad</span>
        </Link>
        <div>
          <ContentLayout pt={true}>
            <div className={styles.medic__content}>
              <div className={styles.medic__inner}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <p className="title">
                    Mentor: {doctorInfo?.firstName} {doctorInfo?.lastName}
                  </p>
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    {/* Commission-percentage pill disabled (2026-10-13), per request.
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'inline-block',
                        backgroundColor: '#ECFDF3',
                        color: '#027A48',
                      }}
                    >
                      {ownCommissionPercent != null
                        ? `Provizija ${ownCommissionPercent}%`
                        : 'Provizija -'}
                    </span> */}
                    <Button
                      className="btn-primary"
                      htmlType="button"
                      type="button"
                      onClick={() => setIsDirectReferralsModalOpen(true)}
                    >
                      Dodati mentori
                    </Button>
                  </span>
                </div>
                <GenericTable
                  data={dataMedicInfo}
                  columns={columnsMedicInfo}
                  grayTheme={false}
                  mobNoScroll={false}
                />
              </div>
              <div className={styles.medic__inner}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <p className="title" style={{ margin: 0 }}>
                    Detalji kupovina
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: '#005844',
                          controlOutline: 'rgba(0, 88, 68, 0.1)',
                          colorPrimaryHover: '#004636',
                        },
                        components: {
                          DatePicker: {
                            activeBorderColor: '#005844',
                            hoverBorderColor: '#005844',
                          },
                        },
                      }}
                    >
                      <RangePicker
                        format="DD.MM.YYYY"
                        onChange={handleDateRangeChange}
                        placeholder={['Datum od', 'Datum do']}
                        allowClear
                      />
                    </ConfigProvider>
                    {isAdmin && selectedRowKeys.length > 0 && (
                      <>
                        {/*<span style={{ fontSize: 13, color: '#475467' }}>
                          {selectedMarkableRows.length}{' '}
                          {selectedMarkableRows.length === 1
                            ? 'stavka izabrana'
                            : 'stavki izabrano'}{' '}
                          · Ukupno za isplatu:{' '}
                          <strong>
                            {formatPriceWithCurrency(selectedMarkableTotal)}
                          </strong>
                        </span>*/}
                        <Button
                          className="btn-primary"
                          disabled={
                            bulkUpdating || selectedMarkableRows.length === 0
                          }
                          onClick={() => setBulkConfirmOpen(true)}
                        >
                          {bulkUpdating
                            ? 'Ažuriranje...'
                            : `Označi ${selectedMarkableRows.length} kao realizovano`}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <Tabs
                  activeKey={activePayoutTab}
                  onChange={handlePayoutTabChange}
                  items={payoutTabItems}
                  className="medics_tab"
                />
                <div
                  className={styles.payoutLegend}
                  style={{ justifyContent: 'flex-end', marginBottom: '12px' }}
                >
                  {legendStatuses.map((s) => (
                    <span key={s.key} className={styles.legendItem}>
                      <span
                        className={`${styles.payoutDot} ${styles[s.key] || ''}`}
                      />
                      {s.label}
                    </span>
                  ))}
                </div>
                <GenericTable
                  data={doctorOrders}
                  columns={columnsOrdersInfo}
                  grayTheme={false}
                  mobNoScroll={false}
                  actions={orderActions}
                  rowClassName={(row) =>
                    row.payoutStatus === 'paid' ? 'row--paid' : ''
                  }
                  rowSelection={
                    isAdmin && activePayoutTab === 'pending'
                      ? {
                          selectedRowKeys,
                          onChange: setSelectedRowKeys,
                          getCheckboxProps: (row) => ({
                            disabled: !isRowMarkable(row),
                            title: payoutStatusLabel(row),
                          }),
                        }
                      : null
                  }
                  emptyState={{
                    icon: '/assets/icons/no-data.svg',
                    title:
                      activePayoutTab === 'paid'
                        ? 'Nema realizovanih provizija'
                        : 'Nema provizija koje čekaju realizaciju',
                  }}
                />
                <Pagination
                  currentPage={currentPageOrders}
                  totalPages={totalPagesOrders}
                  onPageChange={onPageChangeOrders}
                />
              </div>
              <div style={{ marginTop: 32 }}>
                <p className="title" style={{ marginBottom: 16 }}>
                  Humanitarne donacije preko ovog mentora
                </p>
                <DoctorCharityEarnings doctorId={id} />
              </div>
            </div>
          </ContentLayout>
        </div>
      </Container>
      {isAdmin && (
        <BulkMarkPaidConfirmModal
          visible={bulkConfirmOpen}
          onClose={() => setBulkConfirmOpen(false)}
          onConfirm={handleBulkMarkPaid}
          count={selectedMarkableRows.length}
          totalAmount={selectedMarkableTotal}
          loading={bulkUpdating}
        />
      )}
      <DirectReferralsModal
        isOpen={isDirectReferralsModalOpen}
        onClose={() => setIsDirectReferralsModalOpen(false)}
        doctorId={id}
      />
    </>
  );
};

export default ViewMedic;
